import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faPen, faPlus, faCheck } from '@fortawesome/free-solid-svg-icons';
import ColumnVisibilityModal from '../../../components/ColumnVisibilityModal';
import { Link, useSearchParams } from 'react-router-dom';
import Table from '../../../components/Table';
import Pagination from '../../../components/Pagination';
import ContaPagarFilterBar from '../../../components/ContaPagarFilterBar';
import { fetchContasPagar, fetchContasPagarStats, baixarContaPagar } from '../../../services/contaPagarService';

const currencyFormatter = new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' });

// Formata datas sem sofrer shift de timezone para strings no formato YYYY-MM-DD
const formatDate = (value) => {
  if (!value) return '-';
  try {
    // Se já for Date
    if (value instanceof Date) return value.toLocaleDateString('pt-BR');

    // Se for string no formato ISO sem hora (YYYY-MM-DD), criar Date local
    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [y, m, d] = value.split('-').map(Number);
      return new Date(y, m - 1, d).toLocaleDateString('pt-BR');
    }

    // Caso geral, deixa o Date tentar parse (ex: datetime com timezome)
    const dt = new Date(value);
    if (isNaN(dt.getTime())) return String(value);
    return dt.toLocaleDateString('pt-BR');
  } catch (err) {
    return String(value);
  }
};

const columns = [
  { Header: 'ID', accessor: 'id' },
  { Header: 'Descrição', accessor: 'descricao' },
  { Header: 'Data Vencimento', accessor: 'dataVencimento', Cell: (value) => formatDate(value) },
  { Header: 'Valor', accessor: 'valor', Cell: (value) => currencyFormatter.format(Number(value) || 0) },
  { Header: 'Parcela', accessor: 'parcela' },
  { Header: 'Total Parcelas', accessor: 'totalParcelas' },
  { Header: 'Status', accessor: 'pago', Cell: (value) => (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${
      value === 'Sim' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
    }`}>
      {value === 'Sim' ? 'Pago' : 'Pendente'}
    </span>
  )},
  { Header: 'Data Pagamento', accessor: 'dataPagamento', Cell: (value) => value ? formatDate(value) : '-' },
  { Header: 'Conta', accessor: 'conta' },
  { Header: 'Cadastrado Por', accessor: 'cadastradoPor' },
  { Header: 'Data Cadastro', accessor: 'dataInclusao', Cell: (value) => formatDate(value) }
];

const pageSizes = [5, 10, 20, 50];

const ContasPagarList = () => {
  // Permite chegar nesta tela com filtros pré-aplicados via querystring
  // (ex.: /financeiro/contas-pagar?pago=Não — drill-down do Dashboard Admin).
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState({
    descricao: searchParams.get('descricao') || '',
    pago: searchParams.get('pago') || '',
    dataVencimentoInicio: searchParams.get('dataVencimentoInicio') || '',
    dataVencimentoFim: searchParams.get('dataVencimentoFim') || '',
    valorMin: searchParams.get('valorMin') || '',
    valorMax: searchParams.get('valorMax') || '',
    conta: searchParams.get('conta') || '',
    cadastradoPor: searchParams.get('cadastradoPor') || ''
  });
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(pageSizes[1]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [visibleColumns, setVisibleColumns] = useState(columns.map(col => col.accessor));
  const [contasPagar, setContasPagar] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Carregar dados
  useEffect(() => {
    const loadContasPagar = async () => {
      try {
        setLoading(true);
        const [contasData, statsData] = await Promise.all([
          fetchContasPagar(),
          fetchContasPagarStats()
        ]);
        setContasPagar(contasData);
        setStats(statsData);
      } catch (err) {
        setError('Erro ao carregar contas a pagar');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    loadContasPagar();
  }, []);

  // Modal handlers
  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);
  const handleToggleColumn = (accessor) => {
    setVisibleColumns(prev =>
      prev.includes(accessor)
        ? prev.filter(col => col !== accessor)
        : [...prev, accessor]
    );
  };

  // Filtros
  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilters({ 
      descricao: '', 
      pago: '', 
      dataVencimentoInicio: '',
      dataVencimentoFim: '',
      valorMin: '',
      valorMax: '',
      conta: '',
      cadastradoPor: ''
    });
    setPage(1);
  };

  // Baixar conta
  const handleBaixarConta = async (id) => {
    if (window.confirm('Confirma a baixa desta conta?')) {
      try {
        const dataPagamento = new Date().toISOString().split('T')[0];
        await baixarContaPagar(id, dataPagamento, 'usuario.atual'); // TODO: pegar usuário logado
        
        // Recarregar dados
        const [contasData, statsData] = await Promise.all([
          fetchContasPagar(),
          fetchContasPagarStats()
        ]);
        setContasPagar(contasData);
        setStats(statsData);
      } catch (err) {
        alert('Erro ao baixar conta');
        console.error(err);
      }
    }
  };

  // Aplicar filtros
  const filteredData = contasPagar.filter(conta => {
    const matchDescricao = !filters.descricao || 
      conta.descricao.toLowerCase().includes(filters.descricao.toLowerCase());
    
    const matchPago = !filters.pago || conta.pago === filters.pago;
    
    const matchDataVencimentoInicio = !filters.dataVencimentoInicio || 
      new Date(conta.dataVencimento) >= new Date(filters.dataVencimentoInicio);
    
    const matchDataVencimentoFim = !filters.dataVencimentoFim || 
      new Date(conta.dataVencimento) <= new Date(filters.dataVencimentoFim);
    
    const matchValorMin = !filters.valorMin || 
      conta.valor >= parseFloat(filters.valorMin);
    
    const matchValorMax = !filters.valorMax || 
      conta.valor <= parseFloat(filters.valorMax);
    
    const matchConta = (() => {
      if (!filters.conta) return true;
      const needle = String(filters.conta).trim();
      // se for número, comparar com contaId
      if (/^\d+$/.test(needle)) {
        return String(conta.contaId || '').includes(needle);
      }
      // caso contrário, comparar com label/texto
      return String(conta.conta || '').toLowerCase().includes(needle.toLowerCase());
    })();
    
    const matchCadastradoPor = !filters.cadastradoPor || 
      conta.cadastradoPor.toLowerCase().includes(filters.cadastradoPor.toLowerCase());

    return matchDescricao && matchPago && matchDataVencimentoInicio && 
           matchDataVencimentoFim && matchValorMin && matchValorMax && 
           matchConta && matchCadastradoPor;
  });

  // Paginação
  const totalPages = Math.ceil(filteredData.length / pageSize);
  const startIndex = (page - 1) * pageSize;
  const paginatedData = filteredData.slice(startIndex, startIndex + pageSize);

  // Colunas visíveis com ações integradas
  const visibleColumnsConfig = columns.filter(col => visibleColumns.includes(col.accessor)).map(col => {
    if (col.accessor === 'descricao') {
      return {
        ...col,
        Cell: (value, row) => (
          <div className="flex items-center gap-2">
            <span>{value}</span>
            <div className="flex space-x-1">
              <Link
                to={`/financeiro/contas-pagar/${row.id}`}
                className="text-blue-600 hover:text-blue-800 transition-colors p-1 rounded focus:outline-none"
                title="Visualizar"
              >
                <FontAwesomeIcon icon={faEye} />
              </Link>
              <Link
                to={`/financeiro/contas-pagar/${row.id}/edit`}
                className="text-green-600 hover:text-green-800 transition-colors p-1 rounded focus:outline-none"
                title="Editar"
              >
                <FontAwesomeIcon icon={faPen} />
              </Link>
              {row.pago === 'Não' && (
                <button
                  onClick={() => handleBaixarConta(row.id)}
                  className="text-purple-600 hover:text-purple-800 transition-colors p-1 rounded focus:outline-none"
                  title="Baixar Conta"
                >
                  <FontAwesomeIcon icon={faCheck} />
                </button>
              )}
            </div>
          </div>
        )
      };
    }
    return col;
  });

  if (loading) {
    return (
      <div className="w-full bg-white rounded shadow p-6 mt-6">
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full bg-white rounded shadow p-6 mt-6">
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="w-full bg-white rounded shadow p-6 mt-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contas a Pagar</h1>
          <p className="text-gray-600">Gerencie suas contas a pagar</p>
        </div>
        <div className="flex gap-2">
          <Link
            to="/financeiro/contas-pagar/create"
            className="px-4 py-2 rounded bg-green-600 text-white font-semibold shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400 flex items-center gap-2"
          >
            <FontAwesomeIcon icon={faPlus} />
            <span>Nova Conta</span>
          </Link>
        </div>
      </div>

      {/* Estatísticas */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-600">Total de Contas</h3>
            <p className="text-2xl font-bold text-gray-900">{stats.totalContas}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-600">Contas Pagas</h3>
            <p className="text-2xl font-bold text-green-600">{stats.contasPagas}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-600">Contas Pendentes</h3>
            <p className="text-2xl font-bold text-red-600">{stats.contasPendentes}</p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-600">Valor Total</h3>
            <p className="text-2xl font-bold text-gray-900">
              {currencyFormatter.format(stats.valorTotal || 0)}
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-600">Valor Pago</h3>
            <p className="text-2xl font-bold text-green-600">
              {currencyFormatter.format(stats.valorPago || 0)}
            </p>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-600">Valor Pendente</h3>
            <p className="text-2xl font-bold text-red-600">
              {currencyFormatter.format(stats.valorPendente || 0)}
            </p>
          </div>
        </div>
      )}

      <ColumnVisibilityModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        columns={columns}
        visibleColumns={visibleColumns}
        onChange={handleToggleColumn}
      />

      <div className="max-w-full">
        <ContaPagarFilterBar
          filters={filters}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
        />
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 gap-2 max-w-full">
        <div className="order-2 sm:order-1 w-full sm:w-auto">
          <Pagination 
            currentPage={page} 
            totalPages={totalPages} 
            onPageChange={setPage} 
          />
        </div>
        <div className="order-1 sm:order-2 flex items-center w-full sm:w-auto justify-end gap-2">
          <label htmlFor="pageSize" className="text-sm text-gray-700 whitespace-nowrap mr-2">Por página:</label>
          <select
            id="pageSize"
            value={pageSize}
            onChange={(e) => {
              setPageSize(Number(e.target.value));
              setPage(1);
            }}
            className="px-2 py-1 rounded border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
          >
            {pageSizes.map(size => (
              <option key={size} value={size}>{size}</option>
            ))}
          </select>
          <button
            type="button"
            className="flex items-center gap-2 px-3 py-1 rounded bg-green-600 text-white font-medium shadow hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-400"
            onClick={() => setIsModalOpen(true)}
            aria-label="Exibir/Ocultar colunas"
          >
            <FontAwesomeIcon icon={faEye} />
            <span>Colunas</span>
          </button>
        </div>
      </div>

      <div className="max-w-full">
        <Table columns={visibleColumnsConfig} data={paginatedData} />
      </div>

      {filteredData.length === 0 && !loading && (
        <div className="text-center py-8 text-gray-500">
          Nenhuma conta a pagar encontrada.
        </div>
      )}
    </div>
  );
};

export default ContasPagarList;
